/* eslint-disable indent */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Button, Grid, Typography, Box, Chip } from '@material-ui/core';
import _ from 'lodash';
import { OrganizationDropdown, PropertyDropdown, FieldDropdown } from 'components';
import { useProperties, useFields, useOrganizations, useSettings } from 'hooks';
import { DashboardHelpers } from 'utils';
import { red } from '@mui/material/colors';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  chipLists: {
    overflow: 'auto',
    maxHeight: '50vh',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40%',
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
    bgcolor: 'background.white',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
  helperTextLong: { fontSize: 10, marginBottom: 10, color: red[600] },
  helperText: { fontSize: 12, marginBottom: 10, color: red[600] },
}));

const DevicesEditModalForm = ({ selectedDevices, removeSelectedDevice, submitEdits, hide, fields }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isSuper } = useSettings();

  const { properties, selectedProperty, fetchProperties } = useProperties();
  const { selectedField, fetchFields } = useFields();
  const { organizations, selectedOrganization } = useOrganizations();

  const [localSelectedOrganization, setLocalSelectedOrganization] = useState(
    Object.keys(selectedOrganization).length > 0
      ? selectedOrganization
      : { id: DashboardHelpers.StatusCodes.NoOrganizationSelected, name: t('none_selected') }
  );

  const [localSelectedProperty, setLocalSelectedProperty] = useState(
    selectedProperty
      ? selectedProperty
      : { id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }
  );
  const [localSelectedField, setLocalSelectedField] = useState(
    selectedField
      ? selectedField
      : {
          id: DashboardHelpers.StatusCodes.NoFieldSelected,
          name: t('none_selected'),
        }
  );

  const [orgError, setOrgError] = useState(false);

  const fieldUnselectedWithProp =
    localSelectedProperty.id !== DashboardHelpers.StatusCodes.NoPropertySelected &&
    (localSelectedField.id === DashboardHelpers.StatusCodes.NoFieldSelected || !localSelectedField);

  const displayedChips =
    selectedDevices &&
    _.size(selectedDevices) > 0 &&
    selectedDevices.map((device) => {
      const f = _.find(fields, { id: device.field_id });
      const dname = device.name?.trim().length > 0 ? device.name : t('none');
      const fname = f ? f.name : t('none');
      return (
        <Chip
          key={device.hw_address}
          label={'Device: ' + device.hw_address.slice(-8) + '; Field: ' + fname + '; Name: ' + dname}
          onDelete={() => {
            deleteDevice(device);
          }}
        />
      );
    });

  const selectOrganization = (organization) => {
    if (organization.id !== localSelectedOrganization.id) {
      setLocalSelectedOrganization(organization);
      fetchProperties(organization.id);
      fetchFields(undefined, organization.id);
      setLocalSelectedProperty({ id: DashboardHelpers.StatusCodes.NoPropertySelected, name: t('none_selected') }); // Reset property if organization is changed
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }); // Reset field if property is changed
    }
  };
  const selectProperty = (property) => {
    if (property.id !== DashboardHelpers.StatusCodes.NoPropertySelected) {
      if (
        !localSelectedOrganization?.id ||
        localSelectedOrganization.id === DashboardHelpers.StatusCodes.NoOrganizationSelected
      ) {
        const tempSelectedOrganization = _.find(organizations, { id: property.organization_id });
        setLocalSelectedOrganization(tempSelectedOrganization);
      }
      if (property?.id !== localSelectedProperty?.id) {
        setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }); // Reset field if property is changed
        setLocalSelectedProperty(property);
      }
    }
  };

  const selectField = (field) => {
    if (field.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
      if (!localSelectedProperty?.id || localSelectedProperty.id === DashboardHelpers.StatusCodes.NoPropertySelected) {
        const localsSectedProperty = _.find(properties, { id: field.property_id });
        selectProperty(localsSectedProperty);
      }
      setLocalSelectedField(field);
    }
  };

  const handleSubmit = () => {
    if (
      !localSelectedOrganization ||
      localSelectedOrganization.id === DashboardHelpers.StatusCodes.NoOrganizationSelected
    ) {
      setOrgError(true);
    } else {
      let changes = {};
      if (localSelectedField && localSelectedField.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
        changes.field_id = localSelectedField.id;
      } else {
        changes.field_id = null;
      }
      if (isSuper) {
        changes.organization_id = localSelectedOrganization.id;
      }
      changes.device_id = _.map(selectedDevices, 'id');
      submitEdits(changes);

      hide();
    }
  };

  const deleteDevice = (device) => {
    removeSelectedDevice(device);
  };
  return (
    <Card className={classes.modal}>
      <CardContent>
        <form
          onSubmit={() => {
            handleSubmit();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={12}>
              <Typography variant="h3">{t('devices_edit')}</Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
              <Box className={classes.chipLists}>{displayedChips}</Box>
            </Grid>
            {isSuper && (
              <Grid item xs={12} sm={6}>
                <OrganizationDropdown
                  setSelectedOrganization={selectOrganization}
                  selectedOrganization={localSelectedOrganization}
                />
                {orgError && (
                  <Typography align="center" className={classes.helperText}>
                    {t('required')}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <PropertyDropdown
                selectedProperty={localSelectedProperty}
                setSelectedProperty={selectProperty}
                properties={properties}
                selectedOrganization={localSelectedOrganization}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldDropdown
                setSelectedField={selectField}
                selectedField={localSelectedField}
                selectedProperty={localSelectedProperty}
                selectedOrganization={localSelectedOrganization}
              />
              <Grid item xs={12} sm={6}>
                {fieldUnselectedWithProp && (
                  <Typography align="center" className={classes.helperTextLong}>
                    {t('field_select_required_with_property')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Button onClick={hide} color="primary" className={classes.modalActionCancel}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={fieldUnselectedWithProp}
            variant="contained"
            className={classes.modalActionPrimary}
          >
            {t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

DevicesEditModalForm.propTypes = {
  selectedDevices: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  fields: PropTypes.array,
  deviceDefs: PropTypes.array,
  submitEdits: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  removeSelectedDevice: PropTypes.func.isRequired,
};

DevicesEditModalForm.defaultProps = {
  fields: [],
  selectedDevices: [],
};

export default DevicesEditModalForm;
